import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Flex } from '@chakra-ui/react'
import { MdFileDownload } from 'react-icons/md'
import { UiPagination } from './UiPagination'
import { UiTable } from './UiTable'
import { OrderByDirection } from './UiTableOrderByIcon'

export const UiDataTable = ({
  tableConfig,
  updateTableConfig,
  rows,
  rowKey,
  columns,
  selected,
  allSelected,
  onSelect,
  isLoading,
  exportConfig,
  ...rest
}) => {
  const {
    curPage,
    totalPages,
    totalRecords,
    itemsPerPage,
    search,
    orderBy
  } = tableConfig

  const tableRef = useRef(null)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    // const { scrollY } = window
    // const top = tableRef.current ? scrollY + tableRef.current.offsetTop - 100 : 0
    // window.scrollTo(0, 0)
  }, [tableConfig.curPage])

  const handleExport = async () => {
    if (!exportConfig?.request) return
    setIsExporting(true)
    try {
      const data = await exportConfig.request({
        search,
        orderByKey: orderBy?.key,
        direction: orderBy?.direction
      })
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = exportConfig.filename || `export-${Date.now()}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.log(err)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      {exportConfig && (
        <Flex justifyContent="flex-end" mb={2}>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<MdFileDownload />}
            onClick={handleExport}
            isLoading={isExporting}
            loadingText="Exporting..."
          >
            Export CSV
          </Button>
        </Flex>
      )}
      <UiTable
        ref={tableRef}
        totalRecords={totalRecords}
        itemsPerPage={itemsPerPage}
        onSelectItemsPerPage={(e) => updateTableConfig(draft => {
          draft.itemsPerPage = parseInt(e.target.value)
          draft.curPage = 1
        })}
        search={search}
        onSearch={(v) => updateTableConfig(draft => {
          draft.search = v
          draft.curPage = 1
        })}
        rows={rows}
        rowKey={rowKey}
        columns={columns}
        selected={selected}
        allSelected={allSelected}
        onSelect={onSelect}
        isLoading={isLoading}
        orderBy={orderBy}
        onOrderBy={v => updateTableConfig(draft => { draft.orderBy = { ...v } })}
      />
      <UiPagination
        p={4}
        curPage={curPage}
        totalPages={totalPages}
        onSelect={(v) => updateTableConfig(draft => { draft.curPage = v })}
      />
    </>
  )
}

UiDataTable.propTypes = {
  tableConfig: PropTypes.shape({
    curPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalRecords: PropTypes.number.isRequired,
    itemsPerPage: PropTypes.number,
    search: PropTypes.string,
    orderBy: PropTypes.shape({
      key: PropTypes.string,
      direction: PropTypes.oneOf(Object.values(OrderByDirection))
    })
  }),
  updateTableConfig: PropTypes.func,
  /** the rows of data to populate the table */
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** a unique identifier property for each row */
  rowKey: PropTypes.string.isRequired,
  /** the columns used to build out the table */
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    orderBy: PropTypes.string,
    render: PropTypes.func.isRequired
  })),
  /** the selected items in the table */
  selected: PropTypes.arrayOf(PropTypes.object),
  /** makes all items in the table appear selected */
  allSelected: PropTypes.bool,
  /** callback when a checkbox is clicked, returns a row if a row checkbox clicked, returns no row on a select all click */
  onSelect: PropTypes.func,
  /** shows a loading progress bar */
  isLoading: PropTypes.bool,
  /** optional CSV export config: { filename, request(params) => Promise<Blob|string> } */
  exportConfig: PropTypes.shape({
    filename: PropTypes.string,
    request: PropTypes.func
  })
}
